import type { ComponentProps, VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import { Icon } from "../icon/Icon";
import { IconButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { isNil } from "lodash-es";

type SearchBarProps = {
  query: string;
  onQueryChange: (query: string) => void;
} & Omit<ComponentProps<"input">, "value">;

type RootSearchBarProps = SearchBarProps & {
  isFloating: boolean;
  isActive: boolean;
};

const RootSearchBar: VoidComponent<RootSearchBarProps> = (props) => {
  const { t } = useTranslation();
  const hasQuery = createMemo(() => !isNil(props.query) && props.query !== "");

  const searchInput = (
    <input
      value={props.query}
      class="peer w-full truncate rounded-sm bg-surface-default text-base-medium text-primary placeholder:text-placeholder hover:bg-surface-hover focus:text-primary focus-visible:outline-default"
      classList={{
        "pr-3": !hasQuery(),
        "pr-10": hasQuery(),
        "h-9.5 border border-default py-2 pl-7.5": !props.isFloating,
        "h-10.5 py-3 pl-8.5": props.isFloating,
        "rounded-b-none": props.isActive,
      }}
      onInput={(e) => props.onQueryChange(e.target.value)}
      {...props}
    />
  ) as HTMLInputElement;

  return (
    <div class="group relative">
      {searchInput}
      <Show when={props.query}>
        <IconButton
          size="medium"
          aria-label={t(($) => $.SEARCH_INPUT_CLEAR)}
          variant="tertiary"
          icon="x"
          onClick={() => {
            props.onQueryChange("");
            searchInput.focus();
          }}
          class="absolute inset-y-0 right-1 z-10 my-auto"
        />
      </Show>
      <Icon
        class="pointer-events-none absolute inset-y-0 my-auto icon-tertiary peer-focus:icon-secondary"
        classList={{
          "left-2": !props.isFloating,
          "left-3": props.isFloating,
        }}
        name="search"
        type="solid"
        size="md"
      />
    </div>
  );
};

const SearchBar: VoidComponent<SearchBarProps> = (props) => (
  <RootSearchBar isFloating={false} isActive={false} {...props} />
);

type FloatingSearchBarProps = SearchBarProps & { isActive: boolean };

export const FloatingSearchBar: VoidComponent<FloatingSearchBarProps> = (props) => (
  <RootSearchBar isFloating {...props} />
);

export default SearchBar;
